export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  readingTime: string;
  category: string;
  body: string; // markdown-ish, rendered as paragraphs and headings
  faq?: { q: string; a: string }[];
  updatedAt?: string;
  sources?: { label: string; url: string }[];
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "kiedy-kupowac-bilety-lotnicze-najtaniej",
    updatedAt: "2026-10-07",
    sources: [{"label": "Dane Aviasales i ważność cen", "url": "https://support.travelpayouts.com/hc/en-us/articles/203956163-Aviasales-Data-API"}],
    title: "Kiedy kupować bilety lotnicze najtaniej? Poradnik 2026",
    description: "Jak porównywać terminy zakupu bez mitów o najtańszym dniu? Sprawdź daty, lotniska i koszt całej podróży.",
    publishedAt: "2026-04-12",
    readingTime: "6 min",
    category: "Porady",
    body: `## Nie ma jednej najlepszej chwili dla wszystkich lotów

Cena zależy od terminu, popytu, dostępności miejsc i wybranej taryfy. Nie traktuj określonego dnia tygodnia ani liczby tygodni przed wylotem jako gwarancji oszczędności. Historyczne analizy dotyczą określonych rynków i okresów; nie przesądzają o cenie Twojej podróży z Polski.

## Zacznij od planu i budżetu

Ustal miejsce docelowe, możliwe daty i maksymalny koszt całej podróży. Porównaj bilet w obie strony, bagaż, transfer i nocleg. Przy sztywnych terminach rozpocznij sprawdzanie wcześniej, aby mieć czas na porównanie alternatyw.

## Co porównać przed zakupem?

- Wylot i powrót dzień wcześniej lub później, jeśli możesz zmienić plan.
- Inne lotnisko wylotu wraz z kosztami dojazdu.
- Godziny lotu oraz ewentualny dodatkowy nocleg.
- Tę samą liczbę pasażerów i taki sam bagaż w każdej ofercie.

## Czy warto czekać na last minute?

Bliski termin nie oznacza przeceny. Przy ograniczonej liczbie miejsc cena może wzrosnąć albo połączenie może przestać być dostępne. Last minute jest sposobem podróżowania dla osób elastycznych, nie pewną metodą na tani bilet.

## Zapisana cena a aktualna oferta

Okazje Tanie Latanie pochodzą z danych Aviasales zapisanych na podstawie wcześniejszych wyszukiwań. Wynik może się zmienić po ponownym sprawdzeniu. Cenę i warunki konkretnego biletu potwierdź u sprzedawcy przed płatnością.`,
  },
  {
    slug: "najtansze-dni-tygodnia-na-loty",
    updatedAt: "2026-10-07",
    sources: [{"label": "Dane Aviasales i ważność cen", "url": "https://support.travelpayouts.com/hc/en-us/articles/203956163-Aviasales-Data-API"}],
    title: "Najtańsze dni tygodnia na loty z Polski",
    description: "Jak porównywać dni wylotu i powrotu, godziny oraz koszty pobytu bez zakładania stałej oszczędności.",
    publishedAt: "2026-04-05",
    readingTime: "4 min",
    category: "Porady",
    body: `## Sprawdź dni, które pasują do Twojej trasy

Nie ma dnia tygodnia zawsze najtańszego dla wszystkich połączeń. Różnice zależą od sezonu, rozkładu, popytu i dostępności taryf. Nie myl dnia kupowania biletu z dniem, w którym samolot odlatuje: to dwa różne elementy porównania.

## Jak porównać terminy?

Zacznij od wyjazdu, który faktycznie możesz zrealizować. Sprawdź inne dni wylotu i powrotu, zachowując liczbę pasażerów i zakres bagażu. Porównaj także krótszy lub dłuższy pobyt, ale dopisz koszt dodatkowych noclegów.

- Sprawdź weekend oraz sąsiednie dni robocze.
- Porównaj godziny, nie tylko daty.
- Uwzględnij urlop, dojazd i lokalny transport po przylocie.
- Wyszukaj ponownie po zmianie warunków podróży.

## Wczesny lot nie zawsze jest oszczędnością

Tani wylot rano może wymagać taksówki lub noclegu przy lotnisku. Późny powrót może uniemożliwić podróż pociągiem do domu. Warto porównać koszt całego wyjazdu i czas spędzony w drodze.

## Nie opieraj zakupu na stałym procencie oszczędności

Analizy historyczne nie gwarantują konkretnej różnicy cen dla lotów z Polski. Najważniejsze jest aktualne porównanie Twojego połączenia. Jeżeli cena pasuje do budżetu i warunków, nie musisz czekać na domniemany najlepszy dzień.`,
  },
  {
    slug: "bagaz-podreczny-ryanair-wizzair-2026",
    updatedAt: "2026-10-07",
    sources: [{"label": "Ryanair — zasady bagażu", "url": "https://help.ryanair.com/hc/en-us/articles/12888036565521-Ryanair-s-Bag-Policy"}, {"label": "Wizz Air — limity bagażu", "url": "https://www.wizzair.com/en-gb/help-centre/booking-information-and-services/baggage/baggage-allowance"}, {"label": "easyJet — bagaż kabinowy", "url": "https://www.easyjet.com/en/help/baggage/cabin-bags"}],
    title: "Bagaż podręczny w Ryanair, Wizz Air i easyJet — limity 2026",
    description: "Aktualne wymiary i wagi bagażu podręcznego w 2026 roku. Co możesz zabrać za darmo, a za co zapłacisz na lotnisku.",
    publishedAt: "2026-03-28",
    readingTime: "5 min",
    category: "Linie lotnicze",
    body: `## Mała torba i walizka kabinowa to różne usługi

Przed zakupem sprawdź zakres wybranej taryfy. Wymiary uwzględniają zewnętrzne elementy bagażu; mierz torbę po spakowaniu. Poniższe informacje zweryfikowano w oficjalnych zasadach przewoźników. Przed wylotem sprawdź je ponownie dla swojej rezerwacji.

## Ryanair

- Mała torba osobista: **40 × 30 × 20 cm**, do umieszczenia pod siedzeniem.
- Priority & 2 Cabin Bags: mała torba oraz większy bagaż **55 × 40 × 20 cm**, do **10 kg**.
- Bagaż rejestrowany to osobna usługa zależna od wybranej oferty.

Nie przypisuj większej walizce prawa do wniesienia na pokład tylko dlatego, że w ofercie widnieje słowo „bagaż”. Sprawdź, czy kupujesz usługę kabinową czy rejestrowaną.

## Wizz Air

- Torba pod siedzenie: **40 × 30 × 20 cm**, do **10 kg**.
- Większa walizka w ramach WIZZ Priority: **55 × 40 × 23 cm**, do **10 kg**.

Zakres usług i dostępność dodatków sprawdź w swojej taryfie. Nie zakładaj, że walizka dopuszczona przez inną linię ma odpowiedni rozmiar.

## easyJet

- Mały bagaż kabinowy: **45 × 36 × 20 cm**, do **15 kg**, pod siedzenie.
- Duży bagaż kabinowy: **56 × 45 × 25 cm**, do **15 kg**; musi być uwzględniony w uprawnieniach lub dokupiony zgodnie z ofertą.

## Jak uniknąć pomyłki?

1. Sprawdź liczbę sztuk, wymiary i wagę dla obu kierunków.
2. Zmierz bagaż z kółkami, uchwytami i wypchanymi kieszeniami.
3. Porównaj koszt taryfy z bagażem i zakup dodatku osobno.
4. Przy zmianie przewoźnika sprawdź zasady każdego odcinka.

Nie podajemy stałych cen dopłat: zależą od trasy, terminu i usługi. Aktualną kwotę potwierdź u sprzedawcy lub przewoźnika.`,
  },
  {
    slug: "tanie-loty-do-hiszpanii",
    updatedAt: "2026-10-07",
    sources: [{"label": "Dane Aviasales i ważność cen", "url": "https://support.travelpayouts.com/hc/en-us/articles/203956163-Aviasales-Data-API"}, {"label": "Dokumenty podróży obywateli UE", "url": "https://europa.eu/youreurope/citizens/travel/entry-exit/eu-citizen/index_pl.htm"}],
    title: "Tanie loty do Hiszpanii — najlepsze miesiące i kierunki",
    description: "Kiedy lecieć do Hiszpanii, żeby zapłacić mniej. Porównanie cen Barcelona, Madryt, Malaga, Alicante i Wyspy Kanaryjskie.",
    publishedAt: "2026-03-20",
    readingTime: "7 min",
    category: "Kierunki",
    body: `## Najpierw wybierz cel podróży

Hiszpania to różne regiony, lotniska i koszty dotarcia na miejsce. Barcelona, Madryt, Malaga i Alicante nie są zamiennymi punktami przylotu. Wyspy Kanaryjskie wymagają innego planu niż krótki pobyt w kontynentalnym mieście.

## Porównuj terminy razem z noclegami

Nie podajemy stałego cennika dla miesięcy: koszt zależy od wylotu, dostępności i wybranej taryfy. Sprawdź kilka terminów oraz cenę noclegu. Wydarzenie lokalne lub długi weekend może zmienić koszt pobytu, nawet gdy bilet wygląda korzystnie.

## Co sprawdzić przy wyborze lotniska?

- Odległość od miejsca noclegu i koszt transferu.
- Ostatni kurs transportu po przylocie.
- Kod lotniska w rezerwacji, nie tylko nazwę miasta.
- Godziny powrotu i możliwość dojazdu przed odprawą.

## City break czy dłuższy urlop?

Na krótki pobyt wybierz połączenie pozwalające spędzić realny czas na miejscu. Przy wyjeździe na wyspy uwzględnij lokalny transport i bagaż. Porównaj lot w jedną stronę z pełnym kosztem podróży z powrotem.

## Dokumenty i warunki wjazdu

Wymagania sprawdź w oficjalnych informacjach dla swojej narodowości. Obywatele UE mogą podróżować w UE z ważnym dowodem osobistym lub paszportem; dzieci również potrzebują własnego dokumentu. Sama rezerwacja lotu nie zastępuje dokumentów.

## Jak potwierdzić okazję?

Przejdź ze strony kraju do konkretnego miasta lub lotniska, wybierz daty i liczbę osób. Cena zapisana wcześniej w Aviasales może się zmienić. Zakres bagażu i aktualną kwotę potwierdź u sprzedawcy.`,
  },
  {
    slug: "google-flights-vs-skyscanner-vs-tanie-latanie",
    updatedAt: "2026-10-07",
    sources: [{"label": "Dane Aviasales i ważność cen", "url": "https://support.travelpayouts.com/hc/en-us/articles/203956163-Aviasales-Data-API"}],
    title: "Google Flights vs Skyscanner vs Tanie Latanie — która porównywarka jest najlepsza?",
    description: "Jak porównywać tę samą podróż w różnych wyszukiwarkach? Sprawdź daty, lotniska, bagaż i cenę końcową u sprzedawcy.",
    publishedAt: "2026-03-12",
    readingTime: "6 min",
    category: "Porównania",
    body: `## Porównuj tę samą podróż

Żadna wyszukiwarka nie musi mieć wszystkich ofert ani gwarantować najniższej ceny. Różne serwisy mogą pokazywać innych sprzedawców, taryfy i lotniska. Porównanie ma sens, gdy daty, pasażerowie, bagaż oraz kierunek podróży są zgodne.

## Google Flights i Skyscanner

Oba serwisy pozwalają porównywać połączenia i przekierowują do ofert zakupu. Zakres dostępnych narzędzi i ofert może się zmieniać. Sprawdź bieżące możliwości na stronie danego serwisu zamiast zakładać, że opis poradnika obejmuje każdą funkcję.

## Tanie Latanie

Tanie Latanie korzysta z wyszukiwarki Aviasales udostępnianej przez Travelpayouts. Możesz zacząć od lotniska wylotu, kraju, trasy lub sekcji okazji. Ceny okazji pochodzą z zapisanych wyników wcześniejszych wyszukiwań i nie są gwarancją dostępności. Rezerwację kończysz u wybranego sprzedawcy.

## Jak ocenić różnicę ceny?

- Upewnij się, że chodzi o te same daty i lotniska.
- Sprawdź, czy cena obejmuje powrót i wszystkich pasażerów.
- Porównaj bagaż i warunki zmian.
- Potwierdź kwotę końcową oraz walutę płatności u sprzedawcy.

## Którą wyszukiwarkę wybrać?

Użyj tej, która ułatwia porównanie Twojego planu. Dla ważnej podróży warto sprawdzić ofertę w więcej niż jednym miejscu i bezpośrednio u przewoźnika. Nie zakładaj stałej przewagi jednego serwisu ani stałej kwoty oszczędności.`,
  },
  {
    slug: "loty-z-przesiadka-czy-warto",
    updatedAt: "2026-10-07",
    sources: [{"label": "Prawa pasażerów lotniczych w UE", "url": "https://europa.eu/youreurope/citizens/travel/passenger-rights/air/index_pl.htm"}],
    title: "Loty z przesiadką — czy warto oszczędzać?",
    description: "Kiedy przesiadka oznacza realną oszczędność, a kiedy ryzyko spóźnienia, drogi nocleg lub utratę bagażu.",
    publishedAt: "2026-03-04",
    readingTime: "5 min",
    category: "Porady",
    body: `## Cena to tylko część porównania

Połączenie z przesiadką może być tańsze lub droższe od bezpośredniego. Porównaj całkowity czas podróży, koszt posiłków, dojazdu i ewentualnego noclegu. Nie stosuj jednej stałej różnicy cen do wszystkich tras.

## Jedna rezerwacja a oddzielne bilety

Sprawdź, czy odcinki stanowią podróż na jednej rezerwacji czy samodzielną przesiadkę na oddzielnych biletach. Przy oddzielnych biletach opóźnienie pierwszego lotu może narazić Cię na utratę kolejnego. Warunki pomocy zależą od sprzedawcy i ewentualnej ochrony, którą oferuje.

## Ile czasu zostawić?

Nie ma jednego bezpiecznego minimum dla wszystkich lotnisk. Znaczenie mają terminale, kontrola graniczna, odbiór bagażu, ponowna odprawa i godzina zamknięcia bramki. Sprawdź wymagania przewoźnika, lotniska oraz zasady wjazdu lub tranzytu.

- Czy zmieniasz terminal albo całe lotnisko?
- Czy musisz odebrać i ponownie nadać bagaż?
- Czy potrzebujesz wizy lub dokumentu do tranzytu?
- Co dzieje się przy opóźnieniu pierwszego odcinka?

## Nie zakładaj automatycznego transferu bagażu

Potwierdź u sprzedawcy lub przewoźnika, czy bagaż jest nadawany do miejsca docelowego. Przy oddzielnych biletach szczególnie ważne jest sprawdzenie czasu na odbiór i kolejną odprawę.

## Przed płatnością

Przeczytaj warunki rezerwacji i ochrony połączenia. Przy podróży objętej prawami pasażera w UE szczegółowe uprawnienia zależą od okoliczności, przewoźnika i trasy. Nie zakładaj, że każda samodzielna przesiadka daje taką samą ochronę jak podróż na jednym bilecie.`,
  },
];

export function getPostBySlug(slug: string) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
